import React from 'react';
import { Box, List, ListItem, ListItemButton, ListItemIcon, Tooltip, Drawer } from '@mui/material';
import {
    Dashboard as DashboardIcon,
    School as SchoolIcon,
    AccountBalance as AccountBalanceIcon,
    Assessment as AssessmentIcon,
    Settings as SettingsIcon,
    Logout as LogoutIcon,
    Groups as GroupsIcon,
    Chat as ChatIcon,
    Inventory as InventoryIcon,
    PointOfSale as PointOfSaleIcon,
    LocalShipping as SupplierIcon,
    ShoppingCart as PurchaseIcon,
    MonetizationOn as ExpensesIcon,
    People as PayrollIcon,
    AdminPanelSettings as SaaSAdminIcon,
    MenuBook as AcademicsIcon,
    PieChart as BudgetIcon,
    Payment as SupplierPaymentIcon,
    Upgrade as PromotionIcon
} from '@mui/icons-material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import HistoryIcon from '@mui/icons-material/History';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WorkIcon from '@mui/icons-material/Work';
import { useNavigate, useLocation } from 'react-router-dom';

import { useSchoolProfile } from '../../hooks/useSchoolProfile';

const Sidebar = ({ mobileOpen, onClose, isMobile }: any) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { profile } = useSchoolProfile();

    const [user, setUser] = React.useState<any>(null);

    React.useEffect(() => {
        const userInfo = localStorage.getItem('userInfo');
        if (userInfo) {
            const parsed = JSON.parse(userInfo);
            setUser(parsed.user || parsed);
        }
    }, []);

    const permissions = user?.permissions || [];

    const handleLogout = () => {
        localStorage.removeItem('userInfo');
        navigate('/');
    };

    // Helper to get initials
    const getInitials = (name: string) => {
        if (!name) return 'S';
        const parts = name.split(' ');
        if (parts.length > 1) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name.slice(0, 2).toUpperCase();
    };

    const MENU_ITEMS = [
        { text: 'SaaS Admin', icon: <SaaSAdminIcon />, path: '/dashboard/saas-admin', role: 'SAAS_ADMIN' },
        { text: 'Dashboard', icon: <DashboardIcon />, path: user?.role === 'RECEPTIONIST' ? '/dashboard/receptionist' : '/dashboard', permission: 'view_dashboard' },
        { text: 'Students', icon: <SchoolIcon />, path: '/dashboard/students', permission: 'view_students' },
        { text: 'Academics', icon: <AcademicsIcon />, path: '/dashboard/academics', permission: 'view_academics' },
        { text: 'Promotion Wizard', icon: <PromotionIcon />, path: '/dashboard/promotion-wizard', permission: 'manage_students' },
        
        { text: 'Attendance', icon: <EventNoteIcon />, path: '/dashboard/attendance', permission: 'manage_attendance' },
        { text: 'Exams', icon: <TrendingUpIcon />, path: '/dashboard/exams', permission: 'manage_exams' },
        { text: 'Timetable', icon: <CalendarMonthIcon />, path: '/dashboard/timetable', permission: 'view_timetable' },

        { text: 'Fees', icon: <MonetizationOnIcon />, path: '/dashboard/fees', permission: 'view_fees' },
        { text: 'Invoices', icon: <DescriptionIcon />, path: '/dashboard/invoices', permission: 'view_invoices' },
        { text: 'Payments', icon: <AccountBalanceWalletIcon />, path: '/dashboard/payments', permission: 'view_payments' },
        
        { text: 'Expenses', icon: <ExpensesIcon />, path: '/dashboard/expenses', permission: 'view_accounts' },
        { text: 'Human Resources', icon: <WorkIcon />, path: '/dashboard/hr', permission: 'view_accounts' },
        { text: 'Payroll', icon: <PayrollIcon />, path: '/dashboard/payroll', permission: 'view_accounts' },
        { text: 'Budgets', icon: <BudgetIcon />, path: '/dashboard/budgets', permission: 'view_accounts' },
        
        { text: 'Accounts', icon: <AccountBalanceIcon />, path: '/dashboard/accounts', permission: 'view_accounts' },
        { text: 'General Ledger', icon: <MenuBookIcon />, path: '/dashboard/ledger', permission: 'view_ledger' },
        
        { text: 'My Portal', icon: <AccountBalanceWalletIcon />, path: '/dashboard/parent-portal', permission: 'view_dashboard', roles: ['PARENT'] },
        { text: 'Finance', icon: <AccountBalanceWalletIcon />, path: '/dashboard/parent-finance', permission: 'view_reports' },
        
        { text: 'Groups', icon: <GroupsIcon />, path: '/dashboard/groups', permission: 'view_groups' },
        { text: 'Chat', icon: <ChatIcon />, path: '/dashboard/chat', permission: 'view_chat' },
        
        { text: 'Analytics', icon: <TrendingUpIcon />, path: '/dashboard/analytics', permission: 'view_analytics' },
        { text: 'Reports', icon: <AssessmentIcon />, path: '/dashboard/reports', permission: 'view_reports' },
        
        { text: 'Inventory', icon: <InventoryIcon />, path: '/dashboard/inventory', permission: 'view_inventory' },
        { text: 'Suppliers', icon: <SupplierIcon />, path: '/dashboard/suppliers', permission: 'view_inventory' },
        { text: 'Purchases', icon: <PurchaseIcon />, path: '/dashboard/purchases', permission: 'view_inventory' },
        { text: 'Supplier Payments', icon: <SupplierPaymentIcon />, path: '/dashboard/supplier-payments', permission: 'view_inventory' },
        
        { text: 'POS', icon: <PointOfSaleIcon />, path: '/dashboard/pos', permission: 'view_pos' },
        { text: 'Billing Cycle', icon: <PromotionIcon />, path: '/dashboard/fees/billing', permission: 'view_accounts' },
        { text: 'Events', icon: <CalendarMonthIcon />, path: '/dashboard/events', permission: 'view_events' },
        { text: 'System Audit', icon: <HistoryIcon />, path: '/dashboard/audit', permission: 'view_audit_logs', role: 'SAAS_ADMIN' },
    ];

    const filteredItems = MENU_ITEMS.filter(item => {
        const role = user?.role;

        // SAAS Admin exclusively sees the SaaS Admin portal
        if (role === 'SAAS_ADMIN') {
            return item.role === 'SAAS_ADMIN';
        }

        // SuperAdmin & SchoolAdmin see everything except SaaS and Parent exclusive screens
        if (role === 'SUPER_ADMIN' || role === 'SCHOOL_ADMIN') {
            if (item.role === 'SAAS_ADMIN') return false;
            if (item.roles?.includes('PARENT')) return false;
            return true;
        }

        // Logic for Accountant
        if (role === 'ACCOUNTANT') {
            if (item.role === 'SAAS_ADMIN') return false;
            const allowedModules = [
                'Dashboard', 'Students', 'Fees', 'Billing Cycle', 'Invoices', 'Payments', 
                'Expenses', 'Human Resources', 'Payroll', 'Budgets', 'Accounts', 
                'General Ledger', 'Reports', 'Analytics', 'Inventory', 'Suppliers', 
                'Purchases', 'Supplier Payments', 'POS'
            ];
            return allowedModules.includes(item.text);
        }

        // Logic for Teacher
        if (role === 'TEACHER') {
            if (item.role === 'SAAS_ADMIN') return false;
            const allowedModules = [
                'Dashboard', 'Students', 'Academics', 'Attendance', 'Exams', 
                'Timetable', 'Groups', 'Chat', 'Events'
            ];
            return allowedModules.includes(item.text);
        }

        // Logic for Principal (High-level Oversight)
        if (role === 'PRINCIPAL') {
            if (item.role === 'SAAS_ADMIN') return false;
            if (item.roles?.includes('PARENT')) return false;
            return true;
        }

        if (item.role === 'SAAS_ADMIN') return false;
        
        // Safety Fallback: Always show Dashboard to staff if logged in
        if (item.text === 'Dashboard' && role !== 'PARENT' && role !== 'STUDENT') return true;

        return permissions.includes(item.permission);
    });

    const [logoError, setLogoError] = React.useState(false);

    // ... (rest of component logic)

    const sidebarContent = (
        <Box
            sx={{
                width: isMobile ? '100%' : 80,
                height: isMobile ? '100vh' : 'calc(100vh - 40px)',
                bgcolor: '#1a237e',
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
                position: isMobile ? 'relative' : 'fixed',
                left: isMobile ? 0 : 20,
                top: isMobile ? 0 : 20,
                borderRadius: isMobile ? 0 : '24px',
                zIndex: 1200,
                boxShadow: isMobile ? 'none' : '0 8px 32px rgba(0,0,0,0.2)',
                py: 3,
                alignItems: 'center'
            }}
        >
            {/* Logo Area */}
            <Box sx={{ mb: 4 }}>
                <Tooltip title={profile?.name || 'School System'} placement="right">
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            bgcolor: '#ffab00',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: '1.2rem',
                            color: '#1a237e',
                            cursor: 'pointer',
                            overflow: 'hidden'
                        }}
                    >
                        {profile?.logo && !logoError ? (
                            <img
                                src={profile.logo}
                                alt="Logo"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={() => setLogoError(true)}
                            />
                        ) : (
                            getInitials(profile?.name)
                        )}
                    </Box>
                </Tooltip>
            </Box>

            {/* Navigation - Scrollable Area */}
            <Box
                sx={{
                    flex: 1,
                    width: '100%',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    px: 1,
                    '&::-webkit-scrollbar': {
                        width: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: 'rgba(255, 255, 255, 0.2)',
                    },
                }}
            >
                <List sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', alignItems: isMobile ? 'flex-start' : 'center' }}>
                    {filteredItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <ListItem key={item.text} disablePadding sx={{ width: '100%', justifyContent: isMobile ? 'flex-start' : 'center' }}>
                                <Tooltip title={isMobile ? "" : item.text} placement="right">
                                    <ListItemButton
                                        onClick={() => {
                                            navigate(item.path);
                                            if (isMobile) onClose();
                                        }}
                                        sx={{
                                            minWidth: 0,
                                            width: isMobile ? '90%' : 48,
                                            height: 48,
                                            borderRadius: '12px',
                                            justifyContent: isMobile ? 'flex-start' : 'center',
                                            mx: isMobile ? 2 : 'auto',
                                            bgcolor: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                color: isActive ? 'white' : 'rgba(255,255,255,0.6)',
                                                minWidth: 0,
                                                mr: isMobile ? 2 : 0,
                                                justifyContent: 'center'
                                            }}
                                        >
                                            {item.icon}
                                        </ListItemIcon>
                                        {isMobile && (
                                            <Box sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{item.text}</Box>
                                        )}
                                    </ListItemButton>
                                </Tooltip>
                            </ListItem>
                        );
                    })}
                </List>
            </Box>

            <Box sx={{ mt: 2, borderTop: '1px solid rgba(255,255,255,0.05)', pt: 2, display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'flex-start' : 'center', width: '100%' }}>
                {user?.role !== 'PARENT' && (
                    <Tooltip title={isMobile ? "" : "Settings"} placement="right">
                        <ListItemButton
                            onClick={() => {
                                navigate('/dashboard/settings');
                                if (isMobile) onClose();
                            }}
                            sx={{
                                color: 'rgba(255,255,255,0.6)',
                                mb: 2,
                                width: isMobile ? '90%' : 48,
                                mx: 'auto',
                                borderRadius: '12px',
                                justifyContent: isMobile ? 'flex-start' : 'center'
                            }}
                        >
                            <ListItemIcon sx={{ color: 'inherit', minWidth: 0, mr: isMobile ? 2 : 0 }}>
                                <SettingsIcon />
                            </ListItemIcon>
                            {isMobile && <Box sx={{ fontWeight: 600 }}>Settings</Box>}
                        </ListItemButton>
                    </Tooltip>
                )}
                <Tooltip title={isMobile ? "" : "Logout"} placement="right">
                    <ListItemButton
                        onClick={handleLogout}
                        sx={{
                            borderRadius: '12px',
                            width: isMobile ? '90%' : 48,
                            height: 48,
                            mx: 'auto',
                            justifyContent: isMobile ? 'flex-start' : 'center',
                            color: 'rgba(255,255,255,0.6)'
                        }}
                    >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: 0, mr: isMobile ? 2 : 0 }}>
                            <LogoutIcon />
                        </ListItemIcon>
                        {isMobile && <Box sx={{ fontWeight: 600 }}>Logout</Box>}
                    </ListItemButton>
                </Tooltip>
            </Box>
        </Box>
    );

    return (
        <>
            {isMobile ? (
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={onClose}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: 240,
                            bgcolor: '#1a237e',
                            border: 'none'
                        },
                    }}
                >
                    {sidebarContent}
                </Drawer>
            ) : (
                sidebarContent
            )}
        </>
    );
};

export default Sidebar;
